import { useEffect, useState } from 'react';
import {
  TimeField,
  DateInput,
  DateSegment,
  type TimeValue,
} from 'react-aria-components';
import { Time } from '@internationalized/date';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import { roundUpToFiveMinutes } from '@/scheduling/lib/round-up-to-five-minutes';
import type { SlotItem } from '@/scheduling/lib/slot-selection';

/**
 * CustomTimePopover — type any time, get a slot.
 *
 * Lifted OUT of `time-date-selection-view.tsx` for PROD-2455 (Andrew:
 * "we should have the custom slot as the provider one which allow the user to
 * enter the slot time"). It was already actor-agnostic — a pure function of
 * (day, duration, current pick) → `SlotItem`, with nothing practitioner-shaped
 * anywhere in it — so the device rail gets the real component rather than a
 * second copy that would have to be kept in step by hand.
 *
 * On the device rail it reaches a start off the server's grid, exactly as it
 * does for a practitioner: `DeviceCalendarSlots` is cut at the chosen
 * product's minutes (PROD-2456). The booking contract makes a typed time safe:
 * `buildDeviceBookingRequest` sends `start` only and the server derives `end`
 * from the chosen product's minutes.
 */

/**
 * Seed the custom-time field. When re-opening on an existing pick (`initial`),
 * keep that exact time. On a fresh open (no pick) seed from *now*, rounded UP to
 * the next 5-minute division — Flutter parity: its picker snaps `TimeOfDay.now()`
 * to 5-minute steps, so 12:18 opens at 12:20 (already-aligned times stay put; a
 * round-up past :55 rolls into the next hour).
 */
function seedTime(initial: string | null): TimeValue {
  if (initial) {
    const d = new Date(initial);
    return new Time(d.getHours(), d.getMinutes());
  }
  const { hour, minute } = roundUpToFiveMinutes(new Date());
  return new Time(hour, minute);
}

interface CustomTimePopoverProps {
  pickerDate: Date;
  durationMinutes: number;
  initial: string | null;
  onConfirm: (slot: SlotItem) => void;
}

/**
 * Trigger: "Custom" link in the SELECTED SLOT header.
 * Body: a `react-aria-components` `TimeField` with three keyboard-navigable
 * segments (hour / minute / AM-PM) — type a digit or use ↑↓ to adjust.
 * "Set" builds a synthetic SlotItem from `pickerDate + chosen time`.
 */
export function CustomTimePopover({
  pickerDate,
  durationMinutes,
  initial,
  onConfirm,
}: CustomTimePopoverProps) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<TimeValue | null>(() => seedTime(initial));

  // Re-seed every time the popover opens so it reflects the latest pick.
  useEffect(() => {
    if (open) setValue(seedTime(initial));
  }, [open, initial]);

  const apply = () => {
    if (!value) return;
    const start = new Date(pickerDate);
    start.setHours(value.hour, value.minute, 0, 0);
    const end = new Date(start);
    end.setMinutes(start.getMinutes() + durationMinutes);
    // Emit LOCAL-CLOCK, no-Z strings (`YYYY-MM-DDTHH:mm:ss.sss`) — the same
    // shape the backend slot API returns and the book payload ships verbatim
    // (`transform-form-to-request.ts:312`). The backend applies the
    // practice/user timezone itself, so the wire value must be the literal
    // entered time. `Date.toISOString()` (UTC + `Z`) shifted the custom time
    // by the user's offset (e.g. 10:00 in Dubai → `06:00Z`), which the backend
    // then re-converted to the wrong slot — the reported "system books a
    // different time" bug. See `shared/lib/date-format.ts`.
    const startWire = formatLocalDatetimeForWire(start);
    const endWire = formatLocalDatetimeForWire(end);
    onConfirm({
      id: `custom-${startWire}`,
      start: startWire,
      end: endWire,
    });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
          >
            Custom
          </button>
        }
      />
      <PopoverContent align="end" className="w-auto p-3">
        <p className="text-xs font-medium text-text-muted mb-2">
          Enter time
        </p>
        <TimeField value={value} onChange={setValue} hourCycle={12} aria-label="Custom time">
          <DateInput className="flex items-center h-10 px-3 rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] focus-within:border-brand-primary text-base text-text-primary tabular-nums">
            {(segment) => (
              <DateSegment
                segment={segment}
                className={cn(
                  'px-1 outline-none rounded',
                  'data-[focused]:bg-[var(--accent-active-bg)] data-[focused]:text-brand-primary',
                  'data-[placeholder]:text-text-hint',
                  'data-[type=literal]:px-0',
                )}
              />
            )}
          </DateInput>
        </TimeField>
        <div className="flex justify-end gap-3 mt-3">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-sm font-medium text-text-muted hover:text-text-primary px-2 py-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!value}
            className={cn(
              'text-sm font-medium px-3 py-1 rounded-md',
              value ? 'bg-brand-primary text-white hover:bg-interactive-hover' : 'bg-grey-200 text-text-hint cursor-not-allowed',
            )}
          >
            Set
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
